// Blue Basin Cartography — Map Builder (Concierge MVP)
// Drop this file into: assets/map-builder.js
// Requires your section to render the controls with these IDs:
// bbc-map, bbc-search, bbc-theme, bbc-title, bbc-subtitle,
// bbc-size, bbc-orient, bbc-format, bbc-variant,
// bbc-reset, bbc-snapshot, bbc-add, bbc-snapshot-img, bbc-snapshot-wrap, bbc-note, bbc-canvas
//
// Also requires MapLibre on the page:
// <script src="https://unpkg.com/maplibre-gl@3.6.1/dist/maplibre-gl.js"></script>
// <link href="https://unpkg.com/maplibre-gl@3.6.1/dist/maplibre-gl.css" rel="stylesheet"/>

(function () {
  const root = document.getElementById('bbc-map-builder');
  if (!root) {
    console.warn('[BBC] Root #bbc-map-builder not found');
    return;
  }

  // ---- Config pulled from data-attributes on the section root ----
  const MAPTILER_KEY = root.dataset.maptilerKey || '';                 // required
  const STYLES = {
    urban: root.dataset.styleUrban || 'streets-v2',
    wilderness: root.dataset.styleWilderness || 'outdoor-v2',
    college: root.dataset.styleCollege || 'toner-v2'
  };
  const UPLOAD_ENDPOINT = root.dataset.uploadEndpoint || '';            // optional

  // ---- Element references ----
  const els = {
    map: document.getElementById('bbc-map'),
    search: document.getElementById('bbc-search'),
    theme: document.getElementById('bbc-theme'),
    title: document.getElementById('bbc-title'),
    subtitle: document.getElementById('bbc-subtitle'),
    size: document.getElementById('bbc-size'),
    orient: document.getElementById('bbc-orient'),
    format: document.getElementById('bbc-format'),
    variant: document.getElementById('bbc-variant'),
    reset: document.getElementById('bbc-reset'),
    snapshot: document.getElementById('bbc-snapshot'),
    add: document.getElementById('bbc-add'),
    snapshotImg: document.getElementById('bbc-snapshot-img'),
    snapshotWrap: document.getElementById('bbc-snapshot-wrap'),
    note: document.getElementById('bbc-note'),
    canvas: document.getElementById('bbc-canvas')
  };

  function showNote(msg) {
    if (els.note) els.note.textContent = msg || '';
  }

  // ---- Map init ----
  if (typeof maplibregl === 'undefined') {
    console.error('[BBC] MapLibre not loaded. Include script/css before this file.');
    return;
  }
  if (!MAPTILER_KEY) {
    console.warn('[BBC] Missing MapTiler key — set it on the section in Theme Editor.');
  }

  const map = new maplibregl.Map({
    container: 'bbc-map',
    style: `https://api.maptiler.com/maps/${STYLES.urban}/style.json?key=${MAPTILER_KEY}`,
    center: [-119.9772, 39.0968], // Lake Tahoe-ish default
    zoom: 9
  });
  map.addControl(new maplibregl.NavigationControl(), 'top-right');

  // ---- Helpers ----
  async function geocode(q) {
    if (!q || !MAPTILER_KEY) return null;
    const url = `https://api.maptiler.com/geocoding/${encodeURIComponent(q)}.json?key=${MAPTILER_KEY}`;
    const r = await fetch(url);
    if (!r.ok) return null;
    const j = await r.json();
    if (!j.features?.length) return null;
    const f = j.features[0];
    return { lng: f.center[0], lat: f.center[1], bbox: f.bbox || null, name: f.place_name || f.text };
  }

  function currentStyleId() {
    const chosen = els.theme?.value || 'urban';
    return STYLES[chosen] || STYLES.urban;
  }

  // Pixel dimensions for 300 DPI; we clamp for preview to avoid static API limits.
  function dimsForSize(size, orient) {
    const px = {
      '8x10':  [2400, 3000],
      '12x16': [3600, 4800],
      '16x20': [4800, 6000],
      '18x24': [5400, 7200],
      '24x36': [7200, 10800] // may exceed some providers; we preview downscaled
    }[size] || [3600, 4800];

    let [w, h] = px;
    if (orient === 'landscape') [w, h] = [h, w];

    // Clamp preview to a safe max dimension
    const MAX = 8192; // safe upper bound for most Static APIs
    const scale = Math.min(1, MAX / Math.max(w, h));
    const clamped = { w: Math.round(w * scale), h: Math.round(h * scale), previewScaled: scale < 1 };
    return clamped;
  }

  function buildStaticUrl() {
    const center = map.getCenter();
    const zoom = Math.max(0, Math.min(22, map.getZoom()));
    const styleId = currentStyleId();
    const { w, h, previewScaled } = dimsForSize(els.size.value, els.orient.value);
    const bearing = 0; // lock to north-up for print

    if (previewScaled) {
      showNote('Preview downscaled to fit provider limits. Final print will be full 300 DPI.');
    }

    const url =
      `https://api.maptiler.com/maps/${styleId}/static/` +
      `${center.lng},${center.lat},${zoom},${bearing}/${w}x${h}.png?key=${MAPTILER_KEY}`;

    return { url, w, h, center, zoom, styleId };
  }

  async function composePng() {
    const { url, w, h, center, zoom, styleId } = buildStaticUrl();
    const canvas = els.canvas;
    const ctx = canvas.getContext('2d');

    // Prepare canvas
    canvas.width = w; canvas.height = h;

    // Load base map image
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });

    ctx.drawImage(img, 0, 0);

    // Marginalia (title/subtitle) — 0.5" safe margin at 300 dpi
    const margin = Math.round(0.5 * 300);
    const title = (els.title?.value || '').trim();
    const sub = (els.subtitle?.value || '').trim();

    if (title) {
      ctx.fillStyle = '#0f172a';
      ctx.textAlign = 'center';
      ctx.font = `${Math.round(w * 0.04)}px serif`;
      ctx.fillText(title, w / 2, h - margin * 0.9);
    }
    if (sub) {
      ctx.fillStyle = '#334155';
      ctx.textAlign = 'center';
      ctx.font = `${Math.round(w * 0.025)}px sans-serif`;
      ctx.fillText(sub, w / 2, h - margin * 0.5);
    }

    return {
      dataUrl: canvas.toDataURL('image/png'),
      meta: { w, h, center, zoom, styleId, staticUrl: url }
    };
  }

  async function maybeUploadPng(dataUrl) {
    if (!UPLOAD_ENDPOINT) return null;
    try {
      const r = await fetch(UPLOAD_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileBase64: dataUrl,
          filename: `blue-basin-map-${Date.now()}.png`
        })
      });
      if (!r.ok) return null;
      const j = await r.json();
      return j.url || null; // endpoint should return { url: "https://…" }
    } catch (err) {
      console.warn('[BBC] Upload failed; proceeding without file URL', err);
      return null;
    }
  }

  async function addToCart() {
    try {
      const variantId = els.variant?.value;
      if (!variantId) {
        showNote('Pick a product/variant in the section settings.');
        return;
      }
      showNote('Preparing your map…');

      const { dataUrl, meta } = await composePng();
      const uploadedUrl = await maybeUploadPng(dataUrl);

      const props = {
        'Title': (els.title?.value || '').trim(),
        'Subtitle': (els.subtitle?.value || '').trim(),
        'Theme': els.theme?.value || 'urban',
        'Format': els.format?.value || 'Poster',
        'Size': `${els.size.value} ${els.orient.value}`,
        'Center (lng,lat)': `${meta.center.lng.toFixed(6)},${meta.center.lat.toFixed(6)}`,
        'Zoom': meta.zoom.toFixed(2),
        'Style ID': meta.styleId,
        'Static Map URL': meta.staticUrl
      };
      if (uploadedUrl) props['Print File URL'] = uploadedUrl;

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: 1,
            properties: props
          }]
        })
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Cart add failed: ${res.status} ${txt}`);
      }

      window.location.href = '/cart';
    } catch (e) {
      console.error('[BBC] addToCart error', e);
      showNote('Could not add to cart. Open the console for details.');
    }
  }

  // ---- Events ----
  els.theme?.addEventListener('change', () => {
    const styleId = currentStyleId();
    map.setStyle(`https://api.maptiler.com/maps/${styleId}/style.json?key=${MAPTILER_KEY}`);
  });

  els.search?.addEventListener('keydown', async (e) => {
    if (e.key !== 'Enter') return;
    const q = els.search.value.trim();
    if (!q) return;
    const res = await geocode(q);
    if (!res) return;
    if (res.bbox) {
      map.fitBounds([[res.bbox[0], res.bbox[1]], [res.bbox[2], res.bbox[3]]], { padding: 40, duration: 800 });
    } else {
      map.flyTo({ center: [res.lng, res.lat], zoom: Math.max(map.getZoom(), 11) });
    }
  });

  els.reset?.addEventListener('click', () => {
    map.flyTo({ center: [-119.9772, 39.0968], zoom: 9 });
  });

  els.snapshot?.addEventListener('click', async () => {
    try {
      showNote('Generating preview…');
      const { dataUrl } = await composePng();
      if (els.snapshotWrap) els.snapshotWrap.style.display = 'block';
      els.snapshotImg.src = dataUrl;
      els.snapshotImg.style.display = 'block';
      showNote('Preview generated. (Final print uses full 300 DPI.)');
    } catch (e) {
      console.error('[BBC] Preview error', e);
      showNote('Could not generate preview. Try a smaller size or different area.');
    }
  });

  els.add?.addEventListener('click', addToCart);
})();
